import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export interface SpeedDialItem {
  key: string;
  icon: ReactNode;
  label: string;
  enabled: boolean;
  disabledReason?: string;
}

interface SpeedDialFabProps {
  items: SpeedDialItem[];
  onSelect: (key: string) => void;
  'aria-label'?: string;
}

/**
 * Material Design style Speed Dial FAB.
 * - 활성 항목이 1개면 Speed Dial을 생략하고 바로 onSelect 호출
 * - 활성 항목이 0개면 자체 비노출 (parent에서 items 필터 후 전달하거나 이 내부에서 null 반환)
 */
export function SpeedDialFab({
  items,
  onSelect,
  'aria-label': ariaLabel = '새로 만들기',
}: SpeedDialFabProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const enabledItems = items.filter((item) => item.enabled);

  const close = useCallback(() => setOpen(false), []);

  const handleSelect = useCallback(
    (key: string) => {
      close();
      onSelect(key);
    },
    [close, onSelect],
  );

  // 단일 활성 항목: Speed Dial 생략
  const singleActiveKey = enabledItems.length === 1 ? enabledItems[0].key : null;

  const toggle = useCallback(() => {
    if (singleActiveKey) {
      onSelect(singleActiveKey);
      return;
    }
    setOpen((prev) => !prev);
  }, [singleActiveKey, onSelect]);

  // ESC로 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, close]);

  // items 비었으면 렌더하지 않음
  if (items.length === 0) return null;

  const bottomStyle = { bottom: 'calc(var(--bottom-nav-height, 64px) + 16px)' };

  return (
    <div ref={containerRef}>
      {/* Dim overlay — 헤더(z-[60])와 바텀 네비(z-50) 모두 덮도록 z-[62] */}
      {open && (
        <button
          type="button"
          aria-label="메뉴 닫기"
          onClick={close}
          className="fixed inset-0 z-[62] bg-black/40 transition-opacity duration-200"
        />
      )}

      {/* Speed Dial items — dim overlay 위, 헤더(z-[60])보다 위 */}
      {open && (
        <div
          role="menu"
          aria-label={ariaLabel}
          className="fixed right-4 z-[65] flex flex-col-reverse items-end gap-3"
          style={{ bottom: 'calc(var(--bottom-nav-height, 64px) + 88px)' }}
        >
          {items.map((item, index) => {
            const disabled = !item.enabled;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                aria-disabled={disabled}
                aria-label={disabled && item.disabledReason ? `${item.label} (${item.disabledReason})` : item.label}
                title={disabled ? item.disabledReason : undefined}
                disabled={disabled}
                onClick={() => !disabled && handleSelect(item.key)}
                className={`flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-2xl shadow-lg transition-all duration-200 ease-out ${
                  disabled
                    ? 'bg-ink-200 text-ink-500 cursor-not-allowed'
                    : 'bg-white text-ink-900 hover:bg-brand-50 active:bg-brand-100'
                }`}
                style={{
                  transitionDelay: `${index * 50}ms`,
                  opacity: open ? 1 : 0,
                  transform: open ? 'translateY(0)' : 'translateY(8px)',
                }}
              >
                <span className="text-sm font-medium">{item.label}</span>
                <span
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    disabled ? 'text-ink-500' : 'text-brand-600'
                  }`}
                >
                  {item.icon}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Main FAB — 바텀 네비(z-50), dim overlay(z-[55])보다 위 */}
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggle}
        className="fixed right-4 z-[65] w-14 h-14 bg-brand-600 text-white rounded-full shadow-lg hover:bg-brand-700 active:bg-brand-800 flex items-center justify-center"
        style={bottomStyle}
      >
        <span
          className="inline-block transition-transform duration-200 ease-out"
          style={{ transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          <PlusIcon className="w-7 h-7" />
        </span>
      </button>
    </div>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}
